import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Folder, Plus, Search } from 'lucide-react';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import LoadingRing from '../ui-components/LoadingRing';
import { useToast } from '../../hooks/use-toast';
import {
    createFolder,
    FolderPickerItem,
} from '../../services/api/folderService';
import { FolderPathLabel } from './FolderPathLabel';
import { cn } from '../../lib/utils';

export interface FolderPickerLoadResult {
    folders: FolderPickerItem[];
    selectedIds: string[];
}

interface FolderPickerDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    load: () => Promise<FolderPickerLoadResult>;
    apply: (folderIds: string[]) => Promise<void>;
    onApplied?: (folderIds: string[]) => void;
}

export const FolderPickerDialog = ({
    open,
    onOpenChange,
    load,
    apply,
    onApplied,
}: FolderPickerDialogProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [folders, setFolders] = useState<FolderPickerItem[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [initialIds, setInitialIds] = useState<Set<string>>(new Set());
    const [topIds, setTopIds] = useState<Set<string>>(new Set());
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [createSubmitting, setCreateSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        const run = async () => {
            setLoading(true);
            setSearch('');
            setCreating(false);
            setNewTitle('');
            try {
                const result = await load();
                setFolders(result.folders);
                const initial = new Set(result.selectedIds);
                setSelectedIds(initial);
                setInitialIds(initial);
                setTopIds(initial);
            } catch {
                toast({
                    title: t('folders.common.error'),
                    description: t(
                        'folders.foldersForImplant.toasts.loadFailed'
                    ),
                    variant: 'destructive',
                });
            } finally {
                setLoading(false);
            }
        };
        run();
    }, [open, load, t, toast]);

    const toggleFolder = (folderId: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(folderId)) next.delete(folderId);
            else next.add(folderId);
            return next;
        });
    };

    const handleCreate = async () => {
        const title = newTitle.trim();
        if (!title) return;
        setCreateSubmitting(true);
        try {
            const { _id } = await createFolder({ title });
            const result = await load();
            setFolders(result.folders);
            setSelectedIds((prev) => new Set(prev).add(_id));
            setTopIds((prev) => new Set(prev).add(_id));
            setNewTitle('');
            setCreating(false);
        } catch {
            toast({
                title: t('folders.common.error'),
                description: t('folders.foldersForImplant.toasts.createFailed'),
                variant: 'destructive',
            });
        } finally {
            setCreateSubmitting(false);
        }
    };

    const cancelCreate = () => {
        setCreating(false);
        setNewTitle('');
    };

    const handleApply = async () => {
        setSubmitting(true);
        try {
            const ids = [...selectedIds];
            await apply(ids);
            onApplied?.(ids);
            onOpenChange(false);
        } catch {
            toast({
                title: t('folders.common.error'),
                description: t('folders.foldersForImplant.toasts.updateFailed'),
                variant: 'destructive',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const { ordered, topBlockIds } = useMemo(() => {
        const families = new Map<string, FolderPickerItem[]>();
        for (const folder of folders) {
            const familyId = folder.parentId ?? folder._id;
            families.set(familyId, [...(families.get(familyId) ?? []), folder]);
        }
        const topFamilies = [...families.values()].filter((family) =>
            family.some((f) => topIds.has(f._id))
        );
        const otherFamilies = [...families.values()].filter(
            (family) => !topFamilies.includes(family)
        );
        return {
            ordered: [...topFamilies.flat(), ...otherFamilies.flat()],
            topBlockIds: new Set(topFamilies.flat().map((f) => f._id)),
        };
    }, [folders, topIds]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return ordered;
        const matches = (f: FolderPickerItem) =>
            f.title.toLowerCase().includes(q) ||
            (f.parentTitle ?? '').toLowerCase().includes(q);
        const parentsOfMatches = new Set(
            ordered
                .filter((f) => f.parentId && matches(f))
                .map((f) => f.parentId)
        );
        return ordered.filter((f) => matches(f) || parentsOfMatches.has(f._id));
    }, [ordered, search]);

    const nested = useMemo(() => {
        const result: boolean[] = [];
        filtered.forEach((folder, index) => {
            const above = filtered[index - 1];
            const sameBlock =
                !!above &&
                topBlockIds.has(above._id) === topBlockIds.has(folder._id);
            result.push(
                !!folder.parentId &&
                    sameBlock &&
                    (above._id === folder.parentId ||
                        (above.parentId === folder.parentId &&
                            result[index - 1]))
            );
        });
        return result;
    }, [filtered, topBlockIds]);

    const isDirty = useMemo(() => {
        if (selectedIds.size !== initialIds.size) return true;
        for (const id of selectedIds) if (!initialIds.has(id)) return true;
        return false;
    }, [selectedIds, initialIds]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>
                        {t('folders.foldersForImplant.title')}
                    </DialogTitle>
                </DialogHeader>

                <div className="relative">
                    <Search
                        aria-hidden="true"
                        className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle-foreground"
                    />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('folders.foldersForImplant.search')}
                        aria-label={t('folders.foldersForImplant.search')}
                        className="pl-9"
                    />
                </div>

                <div className="max-h-72 overflow-y-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <LoadingRing size="small" />
                        </div>
                    ) : filtered.length === 0 ? (
                        <p className="py-6 text-center text-sm font-medium text-subtle-foreground">
                            {folders.length === 0
                                ? t('folders.foldersForImplant.emptyDept')
                                : t('folders.foldersForImplant.noMatches')}
                        </p>
                    ) : (
                        <ul className="space-y-1">
                            {filtered.map((folder, index) => {
                                const checked = selectedIds.has(folder._id);
                                const above = filtered[index - 1];
                                const underParent = nested[index];
                                const path = folder.parentTitle
                                    ? `${folder.parentTitle} › ${folder.title}`
                                    : folder.title;
                                const startsRest =
                                    !!above &&
                                    topBlockIds.has(above._id) &&
                                    !topBlockIds.has(folder._id);
                                const lastUnderParent =
                                    underParent && !nested[index + 1];
                                return (
                                    <li
                                        key={folder._id}
                                        className={cn(
                                            'relative',
                                            underParent && 'pl-8',
                                            startsRest &&
                                                'mt-2 border-t border-subtle pt-2'
                                        )}
                                    >
                                        {underParent && (
                                            <>
                                                <span
                                                    aria-hidden="true"
                                                    className={cn(
                                                        'absolute -top-1 left-[15px] w-px bg-subtle',
                                                        lastUnderParent
                                                            ? 'h-[calc(50%+4px)]'
                                                            : 'bottom-0'
                                                    )}
                                                />
                                                <span
                                                    aria-hidden="true"
                                                    className="absolute left-[15px] top-1/2 h-px w-[18px] bg-subtle"
                                                />
                                            </>
                                        )}
                                        <label
                                            htmlFor={`folder-${folder._id}`}
                                            className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-card"
                                        >
                                            <Checkbox
                                                id={`folder-${folder._id}`}
                                                checked={checked}
                                                onCheckedChange={() =>
                                                    toggleFolder(folder._id)
                                                }
                                                aria-label={t(
                                                    'folders.foldersForImplant.toggleAria',
                                                    { title: path }
                                                )}
                                            />
                                            {!underParent && (
                                                <Folder
                                                    aria-hidden="true"
                                                    className="h-4 w-4 flex-shrink-0 text-subtle-foreground"
                                                />
                                            )}
                                            <FolderPathLabel
                                                title={folder.title}
                                                parentTitle={
                                                    underParent
                                                        ? undefined
                                                        : folder.parentTitle
                                                }
                                                className="flex-1 text-sm text-foreground"
                                            />
                                        </label>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                {creating ? (
                    <div className="flex items-center gap-2">
                        <Input
                            autoFocus
                            value={newTitle}
                            onChange={(e) => setNewTitle(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleCreate();
                                }
                                if (e.key === 'Escape') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    cancelCreate();
                                }
                            }}
                            placeholder={t(
                                'folders.foldersForImplant.folderNamePlaceholder'
                            )}
                            aria-label={t(
                                'folders.foldersForImplant.folderNamePlaceholder'
                            )}
                            maxLength={120}
                            disabled={createSubmitting}
                            className="h-9 text-sm"
                        />
                        <Button
                            size="sm"
                            onClick={handleCreate}
                            disabled={!newTitle.trim() || createSubmitting}
                        >
                            {createSubmitting
                                ? t('folders.foldersForImplant.creating')
                                : t('folders.foldersForImplant.create')}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={cancelCreate}
                            disabled={createSubmitting}
                        >
                            {t('folders.foldersForImplant.cancel')}
                        </Button>
                    </div>
                ) : (
                    <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-center"
                        onClick={() => setCreating(true)}
                    >
                        <Plus className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
                        {t('folders.foldersForImplant.newFolder')}
                    </Button>
                )}

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={submitting}
                    >
                        {t('folders.foldersForImplant.cancel')}
                    </Button>
                    <Button
                        onClick={handleApply}
                        disabled={submitting || !isDirty}
                    >
                        {submitting
                            ? t('folders.foldersForImplant.applying')
                            : t('folders.foldersForImplant.apply')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
